import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Eye, EyeOff, AlertTriangle, CheckCircle } from 'lucide-react';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';

const ChangePasswordModal = ({ 
  open, 
  onOpenChange, 
  isForced = false, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
    };
    return checks;
  };

  const passwordChecks = validatePassword(formData.new_password);
  const allChecksPass = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validations
    if (!formData.current_password) {
      setErrors({ current_password: 'Current password is required' });
      return;
    }
    if (!formData.new_password) {
      setErrors({ new_password: 'New password is required' });
      return;
    }
    if (!allChecksPass) {
      setErrors({ new_password: 'Password does not meet all requirements' });
      return;
    }
    if (formData.new_password !== formData.confirm_password) {
      setErrors({ confirm_password: 'Passwords do not match' });
      return;
    }
    if (formData.current_password === formData.new_password) {
      setErrors({ new_password: 'New password must be different from current password' });
      return;
    }

    setLoading(true);
    try {
      await authAPI.changePassword({
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      toast.success('Password changed successfully');
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
      onSuccess?.();
      if (!isForced) {
        onOpenChange(false);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
      if (message.toLowerCase().includes('current password')) {
        setErrors({ current_password: message });
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <Dialog open={open} onOpenChange={isForced ? undefined : onOpenChange}>
      <DialogContent className="max-w-md" onPointerDownOutside={isForced ? (e) => e.preventDefault() : undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-600" />
            {isForced ? 'Password Change Required' : 'Change Password'}
          </DialogTitle>
          <DialogDescription>
            {isForced 
              ? 'For security reasons, you must change your password before continuing. Your current password is your Employee ID.'
              : 'Enter your current password and choose a new secure password.'
            }
          </DialogDescription>
        </DialogHeader>

        {isForced && (
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">First-time Login</p>
              <p className="text-amber-600">Your default password is your Employee ID. Please create a secure password to continue.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {/* Current Password */}
          <div className="space-y-2">
            <Label htmlFor="current_password">Current Password</Label>
            <div className="relative">
              <Input
                id="current_password"
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.current_password}
                onChange={(e) => setFormData(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder={isForced ? "Enter your Employee ID" : "Enter current password"}
                className={errors.current_password ? 'border-red-500' : ''}
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.current_password && (
              <p className="text-sm text-red-500">{errors.current_password}</p>
            )}
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label htmlFor="new_password">New Password</Label>
            <div className="relative">
              <Input
                id="new_password"
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.new_password}
                onChange={(e) => setFormData(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="Enter new password"
                className={errors.new_password ? 'border-red-500' : ''}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.new_password && (
              <p className="text-sm text-red-500">{errors.new_password}</p>
            )}
            
            {/* Password Requirements */}
            {formData.new_password && (
              <div className="grid grid-cols-2 gap-1 text-xs mt-2">
                <div className={`flex items-center gap-1 ${passwordChecks.length ? 'text-green-600' : 'text-slate-400'}`}>
                  <CheckCircle className={`w-3 h-3 ${passwordChecks.length ? 'text-green-600' : 'text-slate-300'}`} />
                  At least 8 characters
                </div>
                <div className={`flex items-center gap-1 ${passwordChecks.uppercase ? 'text-green-600' : 'text-slate-400'}`}>
                  <CheckCircle className={`w-3 h-3 ${passwordChecks.uppercase ? 'text-green-600' : 'text-slate-300'}`} />
                  One uppercase letter
                </div>
                <div className={`flex items-center gap-1 ${passwordChecks.lowercase ? 'text-green-600' : 'text-slate-400'}`}>
                  <CheckCircle className={`w-3 h-3 ${passwordChecks.lowercase ? 'text-green-600' : 'text-slate-300'}`} />
                  One lowercase letter
                </div>
                <div className={`flex items-center gap-1 ${passwordChecks.number ? 'text-green-600' : 'text-slate-400'}`}>
                  <CheckCircle className={`w-3 h-3 ${passwordChecks.number ? 'text-green-600' : 'text-slate-300'}`} />
                  One number
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirm New Password</Label>
            <div className="relative">
              <Input
                id="confirm_password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Re-enter new password"
                className={errors.confirm_password ? 'border-red-500' : ''}
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirm_password && (
              <p className="text-sm text-red-500">{errors.confirm_password}</p>
            )}
            {formData.confirm_password && formData.new_password === formData.confirm_password && (
              <p className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Passwords match
              </p>
            )}
          </div>

          <DialogFooter className="mt-6">
            {!isForced && (
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            )}
            <Button 
              type="submit" 
              disabled={loading || !allChecksPass || formData.new_password !== formData.confirm_password}
              data-testid="change-password-submit"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordModal;
